//#region tokens/typography.ts
/**
* EITELKEIT Typography Tokens
* ═══════════════════════════════════════════════════════════════════
* JetBrains Mono for technical content, Inter for everything else
*/
const fonts = {
	mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
	sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
};
const fontSizes = {
	xs: "10px",
	sm: "12px",
	md: "13px",
	base: "14px",
	lg: "16px",
	xl: "18px",
	"2xl": "20px",
	"3xl": "24px",
	"4xl": "28px"
};
const fontWeights = {
	normal: 400,
	medium: 500,
	semibold: 600,
	bold: 700
};
const lineHeights = {
	tight: 1.25,
	normal: 1.5,
	relaxed: 1.75
};
const letterSpacings = {
	tight: "-0.01em",
	normal: "0",
	wide: "0.05em",
	wider: "0.1em",
	widest: "0.15em"
};
/**
* Semantic typography presets
*/
const typography = {
	h1: {
		fontFamily: fonts.sans,
		fontSize: fontSizes["4xl"],
		fontWeight: fontWeights.bold,
		lineHeight: lineHeights.tight
	},
	h2: {
		fontFamily: fonts.sans,
		fontSize: fontSizes["2xl"],
		fontWeight: fontWeights.semibold,
		lineHeight: lineHeights.tight
	},
	h3: {
		fontFamily: fonts.sans,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.semibold,
		lineHeight: lineHeights.tight
	},
	body: {
		fontFamily: fonts.sans,
		fontSize: fontSizes.base,
		fontWeight: fontWeights.normal,
		lineHeight: lineHeights.normal
	},
	bodySmall: {
		fontFamily: fonts.sans,
		fontSize: fontSizes.md,
		fontWeight: fontWeights.normal,
		lineHeight: lineHeights.normal
	},
	label: {
		fontFamily: fonts.sans,
		fontSize: fontSizes.xs,
		fontWeight: fontWeights.semibold,
		lineHeight: lineHeights.tight,
		textTransform: "uppercase",
		letterSpacing: letterSpacings.wider
	},
	stat: {
		fontFamily: fonts.mono,
		fontSize: fontSizes["3xl"],
		fontWeight: fontWeights.bold,
		lineHeight: lineHeights.tight
	},
	statSmall: {
		fontFamily: fonts.mono,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.semibold,
		lineHeight: lineHeights.tight
	},
	code: {
		fontFamily: fonts.mono,
		fontSize: fontSizes.md,
		fontWeight: fontWeights.normal,
		lineHeight: lineHeights.normal
	},
	timestamp: {
		fontFamily: fonts.mono,
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.normal,
		lineHeight: lineHeights.tight
	},
	kbd: {
		fontFamily: fonts.mono,
		fontSize: fontSizes.xs,
		fontWeight: fontWeights.medium,
		lineHeight: lineHeights.tight
	},
	caption: {
		fontFamily: fonts.sans,
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.normal,
		lineHeight: lineHeights.normal
	}
};
//#endregion
//#region tokens/spacing.ts
/**
* EITELKEIT Spacing & Layout Tokens
* ═══════════════════════════════════════════════════════════════════
*/
const spacing = {
	0: "0",
	px: "1px",
	.5: "2px",
	1: "4px",
	2: "8px",
	3: "12px",
	4: "16px",
	5: "20px",
	6: "24px",
	8: "32px",
	10: "40px",
	12: "48px",
	16: "64px",
	xs: "4px",
	sm: "8px",
	md: "12px",
	lg: "16px",
	xl: "24px",
	"2xl": "32px",
	"3xl": "48px"
};
const radii = {
	none: "0",
	xs: "2px",
	sm: "4px",
	md: "8px",
	lg: "12px",
	xl: "16px",
	"2xl": "24px",
	full: "9999px",
	button: "8px",
	input: "8px",
	card: "12px",
	panel: "16px",
	modal: "16px",
	badge: "4px",
	avatar: "9999px"
};
const shadows = {
	none: "none",
	sm: "0 1px 2px rgba(0, 0, 0, 0.3)",
	md: "0 4px 12px rgba(0, 0, 0, 0.4)",
	lg: "0 8px 32px rgba(0, 0, 0, 0.5)",
	glow: "0 0 20px rgba(57, 255, 159, 0.15)",
	glowStrong: "0 0 30px rgba(57, 255, 159, 0.25)",
	mintGlow: "0 0 20px rgba(57, 255, 159, 0.3)",
	mintGlowStrong: "0 0 30px rgba(57, 255, 159, 0.4)",
	glowDanger: "0 0 20px rgba(255, 71, 87, 0.2)",
	glowWarning: "0 0 20px rgba(255, 184, 57, 0.2)",
	glowInfo: "0 0 20px rgba(57, 192, 255, 0.2)",
	focusRing: "0 0 0 3px rgba(57, 255, 159, 0.15)",
	focusRingStrong: "0 0 0 2px #0a0a0b, 0 0 0 4px #39ff9f",
	glass: `
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    inset 0 -1px 0 rgba(0, 0, 0, 0.15)
  `.replace(/\s+/g, " ").trim()
};
const zIndices = {
	base: 0,
	dropdown: 100,
	sticky: 200,
	overlay: 300,
	modal: 400,
	popover: 500,
	tooltip: 600,
	toast: 700,
	max: 9999
};
//#endregion
//#region tokens/motion.ts
/**
* EITELKEIT Motion Tokens
* ═══════════════════════════════════════════════════════════════════
* Animation durations, easings, and keyframes
* Respects prefers-reduced-motion automatically
*/
const durations = {
	/** 0ms - Instant, no transition */
	instant: "0ms",
	/** 50ms - Micro-interactions, toggles */
	fastest: "50ms",
	/** 100ms - Hover states, small elements */
	fast: "100ms",
	/** 150ms - Standard transitions */
	normal: "150ms",
	/** 200ms - Medium transitions */
	moderate: "200ms",
	/** 300ms - Panel slides, modals */
	slow: "300ms",
	/** 400ms - Page transitions */
	slower: "400ms",
	/** 500ms - Complex animations */
	slowest: "500ms",
	/** 1000ms - Ambient effects */
	ambient: "1000ms"
};
const easings = {
	/** Linear - No easing, constant speed */
	linear: "linear",
	/** Ease out - Decelerate, most common for UI */
	easeOut: "cubic-bezier(0.0, 0.0, 0.2, 1)",
	/** Ease in - Accelerate, for exits */
	easeIn: "cubic-bezier(0.4, 0.0, 1, 1)",
	/** Ease in-out - Balanced, for transforms */
	easeInOut: "cubic-bezier(0.4, 0.0, 0.2, 1)",
	/** Productive - Standard UI transitions */
	productive: "cubic-bezier(0.2, 0.0, 0.38, 0.9)",
	/** Expressive - Emphasis, attention-grabbing */
	expressive: "cubic-bezier(0.4, 0.14, 0.3, 1)",
	/** Entrance - Elements appearing */
	entrance: "cubic-bezier(0.0, 0.0, 0.3, 1)",
	/** Exit - Elements leaving */
	exit: "cubic-bezier(0.4, 0.0, 1, 1)",
	/** Spring - Organic bounce feel */
	spring: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
	/** Bounce - Overshoot and settle */
	bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
	/** Sharp - Quick and precise */
	sharp: "cubic-bezier(0.4, 0, 0.6, 1)"
};
const transitions = {
	fast: `${durations.fast} ${easings.productive}`,
	normal: `${durations.normal} ${easings.productive}`,
	slow: `${durations.slow} ${easings.productive}`,
	/** Standard all-property transition */
	all: `all ${durations.normal} ${easings.productive}`,
	/** Fast all-property transition */
	allFast: `all ${durations.fast} ${easings.productive}`,
	/** Slow all-property transition */
	allSlow: `all ${durations.slow} ${easings.productive}`,
	/** Color transitions (background, border, text) */
	colors: `color ${durations.normal} ${easings.productive}, background-color ${durations.normal} ${easings.productive}, border-color ${durations.normal} ${easings.productive}`,
	/** Opacity transitions */
	opacity: `opacity ${durations.normal} ${easings.productive}`,
	opacityFast: `opacity ${durations.fast} ${easings.productive}`,
	/** Transform transitions */
	transform: `transform ${durations.normal} ${easings.productive}`,
	transformFast: `transform ${durations.fast} ${easings.productive}`,
	transformSlow: `transform ${durations.slow} ${easings.expressive}`,
	/** Scale with spring */
	scale: `transform ${durations.moderate} ${easings.spring}`,
	/** Shadow transitions */
	shadow: `box-shadow ${durations.normal} ${easings.productive}`,
	/** None - Disable transitions */
	none: "none"
};
const keyframes = {
	/** Fade in from transparent */
	fadeIn: `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `,
	/** Fade out to transparent */
	fadeOut: `
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }
  `,
	/** Slide in from bottom */
	slideInUp: `
    @keyframes slideInUp {
      from {
        opacity: 0;
        transform: translateY(16px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
	/** Slide in from top */
	slideInDown: `
    @keyframes slideInDown {
      from {
        opacity: 0;
        transform: translateY(-16px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
	/** Slide in from left */
	slideInLeft: `
    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-16px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
  `,
	/** Slide in from right */
	slideInRight: `
    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(16px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
  `,
	/** Scale up from center */
	scaleIn: `
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
  `,
	/** Scale down to center */
	scaleOut: `
    @keyframes scaleOut {
      from {
        opacity: 1;
        transform: scale(1);
      }
      to {
        opacity: 0;
        transform: scale(0.95);
      }
    }
  `,
	/** Pulse animation for loading states */
	pulse: `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `,
	/** Spin animation */
	spin: `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `,
	/** Bounce animation */
	bounce: `
    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
        animation-timing-function: ${easings.bounce};
      }
      50% {
        transform: translateY(-8px);
        animation-timing-function: ${easings.bounce};
      }
    }
  `,
	/** Shake animation for errors */
	shake: `
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
      20%, 40%, 60%, 80% { transform: translateX(4px); }
    }
  `,
	/** Glow pulse for mint accent */
	glowPulse: `
    @keyframes glowPulse {
      0%, 100% {
        box-shadow: 0 0 8px rgba(57, 255, 159, 0.3);
      }
      50% {
        box-shadow: 0 0 16px rgba(57, 255, 159, 0.5);
      }
    }
  `
};
const animations = {
	/** Fade in */
	fadeIn: `fadeIn ${durations.normal} ${easings.entrance} forwards`,
	fadeInFast: `fadeIn ${durations.fast} ${easings.entrance} forwards`,
	fadeInSlow: `fadeIn ${durations.slow} ${easings.entrance} forwards`,
	/** Fade out */
	fadeOut: `fadeOut ${durations.normal} ${easings.exit} forwards`,
	fadeOutFast: `fadeOut ${durations.fast} ${easings.exit} forwards`,
	/** Slide animations */
	slideInUp: `slideInUp ${durations.slow} ${easings.expressive} forwards`,
	slideInDown: `slideInDown ${durations.slow} ${easings.expressive} forwards`,
	slideInLeft: `slideInLeft ${durations.slow} ${easings.expressive} forwards`,
	slideInRight: `slideInRight ${durations.slow} ${easings.expressive} forwards`,
	/** Scale animations */
	scaleIn: `scaleIn ${durations.moderate} ${easings.spring} forwards`,
	scaleOut: `scaleOut ${durations.fast} ${easings.exit} forwards`,
	/** Utility animations */
	pulse: `pulse ${durations.ambient} ${easings.easeInOut} infinite`,
	spin: `spin 1s ${easings.linear} infinite`,
	bounce: `bounce 1s ${easings.easeInOut} infinite`,
	shake: `shake 0.5s ${easings.productive}`,
	glowPulse: `glowPulse 2s ${easings.easeInOut} infinite`
};
/**
* CSS media query for reduced motion preference
* Use in CSS: @media (prefers-reduced-motion: reduce) { ... }
*/
const reducedMotionQuery = "(prefers-reduced-motion: reduce)";
/**
* Get transition value respecting reduced motion preference
* Returns 'none' if reduced motion is preferred
*/
function getTransition(transition) {
	if (typeof window !== "undefined") {
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "none";
	}
	return transition;
}
/**
* Get animation value respecting reduced motion preference
* Returns 'none' if reduced motion is preferred
*/
function getAnimation(animation) {
	if (typeof window !== "undefined") {
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "none";
	}
	return animation;
}
//#endregion
export { lineHeights as _, getTransition as a, transitions as c, spacing as d, zIndices as f, letterSpacings as g, fonts as h, getAnimation as i, radii as l, fontWeights as m, durations as n, keyframes as o, fontSizes as p, easings as r, reducedMotionQuery as s, animations as t, shadows as u, typography as v };

//# sourceMappingURL=motion-Cg-JnSSP.mjs.map